import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2, DOCUMENT, Injector } from '@angular/core';
import { Observable, throwError, BehaviorSubject, take } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { Masquerade, masqueradeToken } from 'go-modules/masquerade/masquerade.service';
import dayjs from 'dayjs';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import {
	ApiGetTourResponse,
	HttpTourService
} from '@go-modules/services/http-tour/http-tour.service';
import {
	GLOBAL_PULSE_TOUR_CLASSES,
	GLOBAL_PULSE_TOUR_STYLE_ID
} from '@go-modules/services/go-pulse-tour/constants/go-pulse-tour';
import { TranslateService } from '@ngx-translate/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { GoCdkPopoverContentComponent } from '@go-modules/directives/go-cdk-popover/go-cdk-popover-content/go-cdk-popover-content.component';

export interface PulseTourConfig {
	viewTrackKey: string;
	steps: PulseTourStep[];
	constraints: {
		/**
		 * The date the tour is "launched" on the site
		 * If `isOnboarding` is true,  all users created after this date will see the tour
		 * If `isOnboarding` is false, all users created before this date will see the tour
		 * DO NOT SET THE DATE FAR IN THE PAST AND SET `isOnboarding` to TRUE
		 */
		tourStartDate: Date;
		/**
		 * Set to true for tours that should be seen by new signups only
		 * Set to false to show new product features to existing users
		 */
		isOnboarding: boolean;
	};
	/**
	 * Indicates whether this tour supports dynamic step addition
	 * Dynamic tours won't auto-complete when reaching the last step
	 */
	isDynamic?: boolean;
}

export interface PulseTourStep {
	completeSelectors: string[];
	pulse?: {
		selectors: string[];
		stylePadding?: StylePadding;
	};
	newBadgeSelectors?: string[];
	/**
	 * Optional popover configuration that will be shown when entering this step
	 */
	popover?: {
		title?: string; // Translation key
		titleIcon?: string; // Icon class (e.g., 'ficon-lightbulb-o')
		titleIconColor?: string; // Icon color (e.g., '#EDCB50')
		body: string; // Translation key
		selector?: string; // Target element for popover (defaults to first pulse selector)
		position?: 'above' | 'below' | 'before' | 'after'; // Popover position
		showArrow?: boolean;
		showCloseButton?: boolean;
	};
}

export interface TourState {
	tourKey: string;
	currentStepIndex: number;
	totalSteps: number;
	isComplete: boolean;
	isDynamic: boolean;
}

interface StylePadding {
	// pixels
	top?: number;
	right?: number;
	bottom?: number;
	left?: number;
}

type MatchingElement = Omit<MatchedElement, 'cleanup'>;

interface MatchedElement {
	element: HTMLElement;
	cleanup: () => void;
	selector: string;
}

interface ElementTracker {
	type: 'pulse' | 'badge' | 'complete';
	selectors: string[];
	matchedElements: MatchedElement[]; // matches at most one element for each selector
	stylePadding?: StylePadding; // applies to all matching pulse elements on current step
}

interface TourStepTracker {
	elementTrackers: ElementTracker[];
}

export interface ActiveTour {
	config: PulseTourConfig;
	currentStepIndex: number;
	stepTrackers: TourStepTracker[]; // actively tracks elements based on the current step index
	popoverOverlayRef?: OverlayRef;
	popoverTargetElement?: HTMLElement;
	stepChange$: BehaviorSubject<TourState>;
	isLastStepAppended?: boolean; // Tracks if the tour ends at the last step (for dynamic tours)
}

@Injectable({
	providedIn: 'root'
})
export class GoPulseTourService implements OnDestroy {
	private readonly observerConfig: MutationObserverInit = {
		childList: true,
		subtree: true,
		attributes: false,
		characterData: false
	};
	private renderer: Renderer2;
	private activeTours: Map<string, ActiveTour> = new Map();
	private inFlightTours = new Set<string>();
	private modifyingTours = new Set<string>();
	public observer: MutationObserver | null = null;

	constructor (
		rendererFactory: RendererFactory2,
		private httpTourService: HttpTourService,
		private translate: TranslateService,
		private featureFlagService: NgxFeatureFlagService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(masqueradeToken) private masquerade: Masquerade,
		@Inject('Window') private window: Window,
		@Inject(DOCUMENT) private document: Document,
		private overlay: Overlay,
		private injector: Injector
	) {
		this.renderer = rendererFactory.createRenderer(null, null);
		if (this.featureFlagService.isAvailable('PULSE_TOUR')) {
			this.injectStyles();
		}
	}

	public cleanupObserver (): void {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
	}

	public ngOnDestroy (): void {
		this.cleanupObserver();
	}

	public startTour (config: PulseTourConfig): Observable<ApiGetTourResponse> {
		if (!this.featureFlagService.isAvailable('PULSE_TOUR')) {
			return throwError(() => new Error('Pulse tour feature flag is not enabled'));
		}

		if (this.inFlightTours.has(config.viewTrackKey)) {
			return throwError(() => new Error('Tour is in flight'));
		}

		const existingTour = this.activeTours.get(config.viewTrackKey);
		if (existingTour) {
			this.cleanupTour(config.viewTrackKey);
		}

		// Lock the tour until async checks are complete
		this.inFlightTours.add(config.viewTrackKey);

		return this.canTourBeViewed(config).pipe(
			finalize(() => this.inFlightTours.delete(config.viewTrackKey)),
			tap(() => {
				const stepTrackers: TourStepTracker[] = this.createTrackersForEachStep(config);

				const initialState: TourState = {
					tourKey: config.viewTrackKey,
					currentStepIndex: -1,
					totalSteps: config.steps.length,
					isComplete: false,
					isDynamic: config.isDynamic || false
				};

				const tour: ActiveTour = {
					config,
					currentStepIndex: -1,
					stepTrackers,
					stepChange$: new BehaviorSubject<TourState>(initialState),
					isLastStepAppended: false // Initialize as false for dynamic tours
				};

				this.activeTours.set(config.viewTrackKey, tour);
				requestAnimationFrame(() => this.nextStep(config.viewTrackKey));
			})
		);
	}

	public cleanupTour (tourKey: string): void {
		const tour = this.activeTours.get(tourKey);
		if (!tour) return;

		// clean up current step trackers
		if (tour.currentStepIndex >= 0 && tour.currentStepIndex < tour.stepTrackers.length) {
			const currentStep = tour.stepTrackers[tour.currentStepIndex];
			currentStep.elementTrackers.forEach((stepElement) => {
				stepElement.matchedElements.forEach(({ cleanup }) => cleanup());
				stepElement.matchedElements = [];
			});
		}

		if (tour.popoverOverlayRef) {
			tour.popoverOverlayRef.dispose();
			tour.popoverOverlayRef = undefined;
			tour.popoverTargetElement = undefined;
		}

		if (tour.stepChange$) {
			tour.stepChange$.complete();
		}

		this.activeTours.delete(tourKey);
		this.inFlightTours.delete(tourKey);
		this.modifyingTours.delete(tourKey);

		if (this.activeTours.size === 0) {
			this.cleanupObserver();
		}
	}

	public appendSteps (config: {
		tourKey: string;
		steps: PulseTourStep[];
		hasLastStep?: boolean;
	}): void {
		const { tourKey, steps, hasLastStep = false } = config;
		const tour = this.activeTours.get(tourKey);

		if (!tour) {
			throw new Error(`Tour ${tourKey} is not active`);
		}

		if (this.modifyingTours.has(tourKey)) {
			throw new Error(`Tour ${tourKey} is currently being modified`);
		}

		if (!tour.config.isDynamic) {
			throw new Error(`Tour ${tourKey} is not configured as dynamic`);
		}

		// Lock tour modification
		this.modifyingTours.add(tourKey);

		try {
			const wasWaitingForSteps = tour.currentStepIndex >= tour.stepTrackers.length;
			tour.config.steps.push(...steps);
			// this tells us if more steps are coming
			tour.isLastStepAppended = hasLastStep;

			const newTrackers = steps.map((step: PulseTourStep) => {
				const elementTrackers: ElementTracker[] = [];

				if (step.pulse?.selectors) {
					elementTrackers.push({
						type: 'pulse',
						selectors: step.pulse.selectors,
						matchedElements: [],
						stylePadding: step.pulse.stylePadding
					});
				}

				if (step.newBadgeSelectors) {
					elementTrackers.push({
						type: 'badge',
						selectors: step.newBadgeSelectors,
						matchedElements: []
					});
				}

				if (step.completeSelectors) {
					elementTrackers.push({
						type: 'complete',
						selectors: step.completeSelectors,
						matchedElements: []
					});
				}

				return { elementTrackers };
			});

			tour.stepTrackers.push(...newTrackers);

			const updatedState: TourState = {
				tourKey: tour.config.viewTrackKey,
				currentStepIndex: tour.currentStepIndex,
				totalSteps: tour.config.steps.length,
				isComplete: false,
				isDynamic: !!tour.config.isDynamic
			};
			tour.stepChange$.next(updatedState);

			// If tour was waiting for steps, continue progression now
			if (wasWaitingForSteps) {
				requestAnimationFrame(() => {
					// Attach elements for the current step that was waiting
					this.attachTourElements(tour, this.document.body);
				});
			}
		} finally {
			this.modifyingTours.delete(tourKey);
		}
	}

	public getTourState (tourKey: string): Observable<TourState> {
		const tour = this.activeTours.get(tourKey);
		if (!tour) {
			return throwError(() => new Error(`Tour ${tourKey} is not active`));
		}
		return tour.stepChange$.asObservable();
	}

	public completeTour (tourKey: string): void {
		const tour = this.activeTours.get(tourKey);
		if (!tour) return;

		this.httpTourService.viewTour(tour.config.viewTrackKey).subscribe({
			complete: () => this.cleanupTour(tour.config.viewTrackKey)
		});
	}

	private createTrackersForEachStep (config: PulseTourConfig): TourStepTracker[] {
		return config.steps.map((step: PulseTourStep) => {
			const elementTrackers: ElementTracker[] = [];

			if (step.pulse?.selectors) {
				elementTrackers.push({
					type: 'pulse',
					selectors: step.pulse.selectors,
					matchedElements: [],
					stylePadding: step.pulse.stylePadding
				});
			}

			if (step.newBadgeSelectors) {
				elementTrackers.push({
					type: 'badge',
					selectors: step.newBadgeSelectors,
					matchedElements: []
				});
			}

			if (step.completeSelectors) {
				elementTrackers.push({
					type: 'complete',
					selectors: step.completeSelectors,
					matchedElements: []
				});
			}

			return { elementTrackers };
		});
	}

	private canTourBeViewed (config: PulseTourConfig): Observable<ApiGetTourResponse> {
		if (!this.validateSteps(config.steps)) {
			return throwError(() => new Error('Invalid tour step configuration'));
		}

		if (this.masquerade.isMasked()) {
			return throwError(() => new Error('Tour not shown to masked users'));
		}

		const userCreatedDate = dayjs.utc(this.userService.currentUser.created);
		const tourStartDate = dayjs.utc(config.constraints.tourStartDate);

		if (config.constraints.isOnboarding) {
			if (userCreatedDate.isBefore(tourStartDate)) {
				return throwError(() => new Error('User was created before onboarding start date'));
			}
		} else {
			if (userCreatedDate.isAfter(tourStartDate)) {
				return throwError(() => new Error('User was created after this tour feature was added'));
			}
		}

		return this.httpTourService.getTour(config.viewTrackKey).pipe(
			map((tourData) => {
				if (tourData?.id) {
					throw new Error('User has seen the tour');
				}
				return tourData;
			})
		);
	}

	private validateSteps (steps: PulseTourStep[]): boolean {
		return steps.every(
			(step) => step.completeSelectors.length && (step.pulse?.selectors.length || step.newBadgeSelectors.length)
		);
	}

	private nextStep (tourKey: string): void {
		const tour = this.activeTours.get(tourKey);
		if (!tour) return;

		// clean up
		if (tour.currentStepIndex >= 0 && tour.currentStepIndex < tour.stepTrackers.length) {
			const currentStep = tour.stepTrackers[tour.currentStepIndex];
			currentStep.elementTrackers.forEach((elementTracker) => {
				elementTracker.matchedElements.forEach(({ cleanup }) => cleanup());
				elementTracker.matchedElements = [];
			});

			if (tour.popoverOverlayRef) {
				tour.popoverOverlayRef.dispose();
				tour.popoverOverlayRef = undefined;
				tour.popoverTargetElement = undefined;
			}
		}

		tour.currentStepIndex++;

		// Check if we're beyond available trackers
		const isAtLastAvailableStep = tour.currentStepIndex >= tour.stepTrackers.length;

		// For dynamic tours, we need to check if this is truly the end or if more steps are coming
		if (tour.config.isDynamic && isAtLastAvailableStep) {
			if (!tour.isLastStepAppended) {
				const waitingState: TourState = {
					tourKey: tour.config.viewTrackKey,
					currentStepIndex: tour.currentStepIndex,
					totalSteps: tour.config.steps.length,
					isComplete: false,
					isDynamic: !!tour.config.isDynamic
				};
				tour.stepChange$.next(waitingState);
				return;
			}
		}

		const isTourComplete = tour.currentStepIndex >= tour.config.steps.length;
		if (isTourComplete) {
			const finalState: TourState = {
				tourKey: tour.config.viewTrackKey,
				currentStepIndex: tour.currentStepIndex - 1,
				totalSteps: tour.config.steps.length,
				isComplete: true,
				isDynamic: !!tour.config.isDynamic
			};

			tour.stepChange$.next(finalState);
			this.completeTour(tour.config.viewTrackKey);
			return;
		}

		// Normal step progression - we have a tracker for this step
		const tourState: TourState = {
			tourKey: tour.config.viewTrackKey,
			currentStepIndex: tour.currentStepIndex,
			totalSteps: tour.config.steps.length,
			isComplete: false,
			isDynamic: !!tour.config.isDynamic
		};

		tour.stepChange$.next(tourState);
		// immediately attempt to attach elements to the DOM
		this.attachTourElements(tour, this.document.body);

		if (!this.observer) {
			this.observer = new this.window.MutationObserver((mutations) => {
				if (!this.activeTours.size) return;
				this.processMutations(mutations);
			});
			this.observer.observe(this.document.body, this.observerConfig);
		}
	}

	private displayPopoverForStep (tour: ActiveTour, targetElement: HTMLElement): void {
		if (tour.popoverOverlayRef) return;

		const currentStepConfig = tour.config.steps[tour.currentStepIndex];
		if (!currentStepConfig?.popover) return;

		const position = currentStepConfig.popover.position || 'below';

		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(targetElement)
			.withPositions(this.getPopoverPositions(position))
			.withViewportMargin(8)
			.withFlexibleDimensions(false)
			.withPush(true);

		const overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
			panelClass: 'pulse-tour-popover'
		});
		// for cleanup detection
		tour.popoverTargetElement = targetElement;

		const closePopover = () => {
			if (!overlayRef.hasAttached()) return;
			overlayRef.dispose();
			tour.popoverOverlayRef = undefined;
			tour.popoverTargetElement = undefined;
		};

		const keyboardHandler = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				closePopover();
				return;
			}

			// Close on Enter or Space if focused element is outside popover and pulsing element
			if (event.key === 'Enter' || event.key === ' ') {
				const focusedElement = this.document.activeElement as HTMLElement;
				const popoverElement = overlayRef.overlayElement;

				// Don't close if focus is inside popover (let popover content work normally)
				if (popoverElement?.contains(focusedElement)) {
					return;
				}

				// Don't close if focus is on the pulsing element (nextStep() will clean up popover when tour advances)
				if (targetElement === focusedElement || targetElement.contains(focusedElement)) {
					return;
				}

				// User is activating a different interactive element
				closePopover();
			}
		};

		const outsideClickHandler = (event: MouseEvent) => {
			const clickTarget = event.target as HTMLElement;
			const popoverElement = overlayRef.overlayElement;

			// Don't close if clicking inside popover
			if (popoverElement?.contains(clickTarget)) {
				return;
			}

			// Click goes through naturally
			closePopover();
		};

		this.document.addEventListener('keydown', keyboardHandler);
		this.document.addEventListener('click', outsideClickHandler);

		overlayRef.detachments().pipe(
			take(1)
		).subscribe(() => {
			this.document.removeEventListener('keydown', keyboardHandler);
			this.document.removeEventListener('click', outsideClickHandler);
		});

		const componentPortal = new ComponentPortal(
			GoCdkPopoverContentComponent,
			null,
			this.injector
		);
		const componentRef = overlayRef.attach(componentPortal);

		// Configure popover content
		const instance = componentRef.instance;
		const overlayId = `pulse-tour-popover-${Math.random().toString(36).substring(2, 11)}`;
		const translatedBody = this.translate.instant(currentStepConfig.popover.body);

		componentRef.setInput('id', overlayId);
		componentRef.setInput('title', currentStepConfig.popover.title);
		componentRef.setInput('titleIcon', currentStepConfig.popover.titleIcon);
		componentRef.setInput('titleIconColor', currentStepConfig.popover.titleIconColor);
		componentRef.setInput('contentText', translatedBody);
		componentRef.setInput('showCloseButton', currentStepConfig.popover.showCloseButton !== false);
		componentRef.setInput('closeButtonAriaLabel', this.translate.instant('common_close'));
		componentRef.setInput('showArrow', currentStepConfig.popover.showArrow !== false);
		componentRef.setInput('arrowPosition', `arrow-${position}`);
		componentRef.setInput('bgColor', '#ffffff');
		componentRef.setInput('shadow', true);
		componentRef.setInput('panelClass', 'pulse-tour-popover-panel');
		componentRef.setInput('maxContentHeight', 300);

		instance.updateContent();

		if (currentStepConfig.popover.showCloseButton !== false) {
			instance.closed.pipe(take(1)).subscribe(closePopover);
		}

		// reference for cleanup
		tour.popoverOverlayRef = overlayRef;
		this.injectPopoverStyles();
	}

	private getPopoverPositions (position: string): any[] {
		const positions: Record<string, any[]> = {
			above: [
				{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -12 },
				{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 12 }
			],
			below: [
				{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 12 },
				{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -12 }
			],
			before: [
				{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -12 },
				{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 12 }
			],
			after: [
				{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 12 },
				{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -12 }
			]
		};

		return positions[position];
	}

	private injectPopoverStyles (): void {
		const styleId = 'pulse-tour-popover-styles';
		if (this.document.querySelector(`#${styleId}`)) return;

		const styles = `
			.go-cdk-popover-panel.pulse-tour-popover-panel {
				--pad-x: 24px; /* Override default 12px for more breathing room */
				max-width: 320px;
				z-index: 10001; /* Above pulse elements */
				pointer-events: auto; /* Popover is clickable */
			}
		`;

		const styleElement = this.document.createElement('style');
		styleElement.id = styleId;
		styleElement.textContent = styles;
		this.document.head.appendChild(styleElement);
	}

	private processMutations (mutations: MutationRecord[]): void {
		mutations.forEach((mutation) => {
			if (mutation.type === 'childList') {
				requestAnimationFrame(() => {
					mutation.removedNodes.forEach((node) => {
						if (node instanceof HTMLElement) {
							this.activeTours.forEach((tour) => this.detachTourElements(tour, node));
						}
					});
					mutation.addedNodes.forEach((node) => {
						if (node instanceof HTMLElement) {
							this.activeTours.forEach((tour) => this.attachTourElements(tour, node));
						}
					});
				});
			}
		});
	}

	private detachTourElements (tour: ActiveTour, node: HTMLElement): void {
		if (tour.currentStepIndex < 0 || tour.currentStepIndex >= tour.stepTrackers.length) {
			return;
		}

		const step = tour.stepTrackers[tour.currentStepIndex];
		step.elementTrackers.forEach((stepElement) => {
			stepElement.matchedElements = stepElement.matchedElements.filter(({ element, cleanup }) => {
				if (node === element || node.contains(element)) {
					cleanup();
					return false;
				}
				return true;
			});
		});

		// Check if popover's target element is being removed
		const popoverTargetBeingRemoved = tour.popoverTargetElement &&
			(node === tour.popoverTargetElement || node.contains(tour.popoverTargetElement));

		// Close popover if its target element was removed
		if (popoverTargetBeingRemoved && tour.popoverOverlayRef) {
			tour.popoverOverlayRef.dispose();
			tour.popoverOverlayRef = undefined;
			tour.popoverTargetElement = undefined;
		}
	}

	private attachTourElements (tour: ActiveTour, node: HTMLElement): void {
		if (tour.currentStepIndex < 0 || tour.currentStepIndex >= tour.stepTrackers.length) {
			return;
		}

		const step = tour.stepTrackers[tour.currentStepIndex];
		const currentStepConfig = tour.config.steps[tour.currentStepIndex];
		let pulseElementAttached: HTMLElement | null = null;

		step.elementTrackers.forEach((elementTracker: ElementTracker) => {
			// clean up any elements in the current step that are no longer in the DOM
			elementTracker.matchedElements = elementTracker.matchedElements.filter(({ element, cleanup }) => {
				if (!this.document.contains(element)) {
					cleanup();
					return false;
				}
				return true;
			});
			const matchedSelectors = new Set(elementTracker.matchedElements.map((element) => element.selector));

			this.findMatchingElements(node, elementTracker.selectors).forEach(({ element, selector }) => {
				// skip if element is already matched
				if (!matchedSelectors.has(selector)) {
					const cleanup = this.createElementAndCleanupHandler(
						tour.config.viewTrackKey,
						element,
						elementTracker.type,
						{ padding: elementTracker.stylePadding }
					);
					elementTracker.matchedElements.push({
						element,
						cleanup,
						selector
					});

					// Track the first pulse element that was attached
					if (elementTracker.type === 'pulse' && !pulseElementAttached) {
						pulseElementAttached = element;
					}
				}
			});
		});

		// Display popover immediately when pulse element is attached
		if (pulseElementAttached && currentStepConfig?.popover) {
			requestAnimationFrame(() => {
				this.displayPopoverForStep(tour, pulseElementAttached);
			});
		}
	}

	private findMatchingElements (node: HTMLElement, selectors: string[]): MatchingElement[] {
		const matches: MatchingElement[] = [];

		// For each selector, find exactly one match. Do not use "querySelectorAll" since that messes up specificity
		for (const selector of selectors) {
			// node itself matches
			if (node.matches(selector)) {
				matches.push({ element: node, selector });
				continue;
			}

			// first matching element within node
			const found = node.querySelector(selector);
			if (found instanceof HTMLElement) {
				matches.push({ element: found, selector });
			}
		}

		return matches;
	}

	/**
	 * Perform a DOM operation and return a cleanup handler
	 */
	private createElementAndCleanupHandler (
		tourKey: string,
		element: HTMLElement,
		type: 'pulse' | 'badge' | 'complete',
		config: { padding?: StylePadding }
	): () => void {
		switch (type) {
			case 'pulse': {
				const pulseDiv = this.createPulseElement(element, config.padding);
				return () => {
					if (pulseDiv && pulseDiv.parentNode) {
						this.renderer.removeChild(pulseDiv.parentNode, pulseDiv);
					}
				};
			}

			case 'badge': {
				const badge = this.createBadgeElement(element);
				return () => {
					if (badge && badge.parentNode) {
						this.renderer.removeChild(badge.parentNode, badge);
					}
				};
			}

			case 'complete': {
				const handleClick = () => this.nextStep(tourKey);
				const handleKeydown = (e: KeyboardEvent) => {
					if (e.key === 'Enter') {
						setTimeout(() => this.nextStep(tourKey), 0);
					}
				};

				// Use capture phase (true) to detect clicks before stopPropagation can prevent bubbling
				element.addEventListener('click', handleClick, true);
				element.addEventListener('keydown', handleKeydown, true);

				return () => {
					element.removeEventListener('click', handleClick, true);
					element.removeEventListener('keydown', handleKeydown, true);
				};
			}
		}
	}

	private createPulseElement (element: HTMLElement, padding?: StylePadding): HTMLElement {
		const existingPulse = element.querySelector(`.${GLOBAL_PULSE_TOUR_CLASSES.PULSE}`);
		if (existingPulse) {
			return existingPulse as HTMLElement;
		}

		const computedStyle = this.window.getComputedStyle(element);

		// setting the position of an arbitrary element to "relative" is not safe
		// it's better to break the tour rather than ruin the UI element its targeting
		if (computedStyle.position === 'static') {
			this.renderer.setStyle(element, 'position', 'relative');
		}

		this.renderer.setAttribute(
			element,
			'aria-label',
			this.translate.instant('go-pulse-tour-service_new-feature-aria-label')
		);
		this.renderer.setAttribute(element, 'aria-live', 'polite');

		const pulseElement = this.renderer.createElement('div');
		this.renderer.addClass(pulseElement, `${GLOBAL_PULSE_TOUR_CLASSES.PULSE}`);
		this.renderer.setStyle(pulseElement, 'position', 'absolute');
		this.renderer.setStyle(pulseElement, 'border-radius', computedStyle.borderRadius);

		const topPadding = padding?.top ?? 0;
		const leftPadding = padding?.left ?? 0;
		const bottomPadding = padding?.bottom ?? 0;
		const rightPadding = padding?.right ?? 0;

		this.renderer.setStyle(pulseElement, 'top', `${topPadding}px`);
		this.renderer.setStyle(pulseElement, 'left', `${leftPadding}px`);
		this.renderer.setStyle(pulseElement, 'width', `calc(100% - ${leftPadding + rightPadding}px)`);
		this.renderer.setStyle(pulseElement, 'height', `calc(100% - ${topPadding + bottomPadding}px)`);
		this.renderer.appendChild(element, pulseElement);
		// make visible in next frame after styles are applied
		requestAnimationFrame(() => {
			this.renderer.addClass(pulseElement, 'visible');
		});

		return pulseElement;
	}

	private createBadgeElement (element: HTMLElement): HTMLElement {
		const existingBadge = element.querySelector(`.${GLOBAL_PULSE_TOUR_CLASSES.BADGE}`);
		if (existingBadge) {
			return existingBadge as HTMLElement;
		}
		const textContent = this.translate.instant('common_new').toUpperCase();
		const ariaLabel = this.translate.instant('go-pulse-tour-service_new-feature-aria-label');
		const badge = this.renderer.createElement('span');
		this.renderer.addClass(badge, GLOBAL_PULSE_TOUR_CLASSES.BADGE);
		this.renderer.setProperty(badge, 'textContent', textContent);
		this.renderer.setAttribute(badge, 'aria-label', ariaLabel);
		this.renderer.appendChild(element, badge);
		return badge;
	}

	private injectStyles (): void {
		if (this.document.querySelector(`#${GLOBAL_PULSE_TOUR_STYLE_ID}`)) return;
		const styles = `
			  .${GLOBAL_PULSE_TOUR_CLASSES.PULSE} {
				pointer-events: none;
				z-index: 1;
				border: 2px solid #00C3B5;
				animation: ${GLOBAL_PULSE_TOUR_CLASSES.ANIMATION} 2s infinite steps(60);
				opacity: 0;
				transition: opacity 150ms ease-in-out;
				will-change: opacity;
			  }
			  .${GLOBAL_PULSE_TOUR_CLASSES.PULSE}.visible {
				opacity: 1;
			  }
			  @keyframes ${GLOBAL_PULSE_TOUR_CLASSES.ANIMATION} {
				0% {
				  box-shadow: 0 0 0 0px rgba(0, 240, 222, 0.4);
				}
				100% {
				  box-shadow: 0 0 0 15px rgba(0, 0, 0, 0);
				}
			  }
			  .${GLOBAL_PULSE_TOUR_CLASSES.BADGE} {
				display: inline-flex;
				align-items: center;
				background-color: var(--color-secondary-ultra-light);
				color: #333;
				padding: 2px 6px;
				border-radius: 3px;
				font-size: 12px;
				font-weight: bold;
				margin-left: 8px;
			  }
		`;
		const styleElement = this.document.createElement('style');
		styleElement.id = GLOBAL_PULSE_TOUR_STYLE_ID;
		styleElement.textContent = styles;
		this.document.head.appendChild(styleElement);
	}
}
